import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER, GOOGLE_MAPS_KEY } from "@/lib/maps";
import { MapPin, CheckCircle2 } from "lucide-react";

const OTA_OPTS = ["Booking", "Airbnb", "Expedia", "Vrbo", "Google"];

export default function PropertyForm() {
  const { t, loadProperties, selectProperty, lang } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", address: "", rooms: 5, property_type: "hotel",
    base_price: 100, min_price: 50, max_price: 400, pms: "", channel_manager: "", otas: [],
    booking_url: "",
    latitude: null, longitude: null, formatted_address: "", city: "", province: "",
    region: "", country: "", postcode: "", place_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [ac, setAc] = useState(null);
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleOta = (o) => set("otas", f.otas.includes(o) ? f.otas.filter((x) => x !== o) : [...f.otas, o]);

  const onPlaceChanged = () => {
    if (!ac) return;
    const place = ac.getPlace();
    if (!place || !place.geometry) return;
    const loc = place.geometry.location;
    const comp = {};
    (place.address_components || []).forEach((c) => (c.types || []).forEach((tp) => { comp[tp] = c.long_name; }));
    setF((s) => ({
      ...s,
      address: place.formatted_address || s.address,
      formatted_address: place.formatted_address || "",
      latitude: loc.lat(), longitude: loc.lng(),
      place_id: place.place_id || "",
      city: comp.locality || comp.administrative_area_level_3 || comp.administrative_area_level_2 || "",
      province: comp.administrative_area_level_2 || "",
      region: comp.administrative_area_level_1 || "",
      country: comp.country || "",
      postcode: comp.postal_code || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/properties", {
        ...f, rooms: Number(f.rooms), base_price: Number(f.base_price),
        min_price: Number(f.min_price), max_price: Number(f.max_price),
      });
      await loadProperties();
      selectProperty(data.id);
      toast.success(lang === "it" ? "Struttura creata — mercato generato" : "Property created — market generated");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const geoReady = f.latitude != null && f.longitude != null;

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">{t("addProperty")}</p>
        <h1 className="text-3xl font-bold mb-8">{lang === "it" ? "Nuova struttura" : "New property"}</h1>
        <form onSubmit={submit} className="space-y-6">
          <div><Label>{lang === "it" ? "Nome struttura" : "Property name"}</Label>
            <Input data-testid="prop-name" required value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
          <div><Label>{t("address")}</Label>
            {isLoaded && GOOGLE_MAPS_KEY ? (
              <Autocomplete onLoad={setAc} onPlaceChanged={onPlaceChanged}
                fields={["address_components", "formatted_address", "geometry", "place_id"]}
                options={{ types: ["geocode"] }}>
                <Input data-testid="prop-address" required placeholder={lang === "it" ? "Inizia a digitare e seleziona dall'elenco Google..." : "Start typing and pick from Google..."}
                  value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1" />
              </Autocomplete>
            ) : (
              <Input data-testid="prop-address" required placeholder="Via Roma 1, Rimini, Italia" value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1" />
            )}
            {geoReady ? (
              <p data-testid="geo-detected" className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: "var(--demand-low)" }}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                {lang === "it" ? "Posizione rilevata" : "Location detected"}: {[f.city, f.province, f.region, f.country].filter(Boolean).join(", ")}
                <span className="font-data text-muted-foreground">({f.latitude.toFixed(4)}, {f.longitude.toFixed(4)})</span>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{lang === "it" ? "Seleziona l'indirizzo dall'autocompletamento Google per salvare GPS, città e provincia." : "Pick the address from Google autocomplete to save GPS, city and province."}</p>
            )}</div>
          <div><Label>{lang === "it" ? "Link Booking.com (consigliato)" : "Booking.com link (recommended)"}</Label>
            <Input data-testid="prop-booking-url" placeholder="https://www.booking.com/hotel/..." value={f.booking_url} onChange={(e) => set("booking_url", e.target.value)} className="mt-1" />
            <p className="text-xs text-muted-foreground mt-1">{lang === "it" ? "Revenue AI leggerà profilo, servizi e prezzo pubblico reale della tua struttura per un confronto omogeneo con i competitor." : "Revenue AI will read your property's profile, amenities and real public price for a homogeneous comparison with competitors."}</p></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("rooms")}</Label>
              <Input data-testid="prop-rooms" type="number" min="1" value={f.rooms} onChange={(e) => set("rooms", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("type")}</Label>
              <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="prop-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hotel">Hotel</SelectItem>
                  <SelectItem value="bnb">B&B</SelectItem>
                  <SelectItem value="vacation_home">{lang === "it" ? "Casa vacanza" : "Vacation home"}</SelectItem>
                  <SelectItem value="short_rental">{lang === "it" ? "Affitto breve" : "Short rental"}</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>{t("basePrice")} €</Label>
              <Input data-testid="prop-base" type="number" value={f.base_price} onChange={(e) => set("base_price", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("minPrice")} €</Label>
              <Input data-testid="prop-min" type="number" value={f.min_price} onChange={(e) => set("min_price", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("maxPrice")} €</Label>
              <Input data-testid="prop-max" type="number" value={f.max_price} onChange={(e) => set("max_price", e.target.value)} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("pms")}</Label>
              <Input data-testid="prop-pms" value={f.pms} onChange={(e) => set("pms", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("channelManager")}</Label>
              <Input data-testid="prop-cm" value={f.channel_manager} onChange={(e) => set("channel_manager", e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label>{t("otas")}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {OTA_OPTS.map((o) => (
                <button type="button" key={o} data-testid={`ota-${o.toLowerCase()}`} onClick={() => toggleOta(o)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.otas.includes(o) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>{o}</button>
              ))}
            </div>
          </div>
          <Button data-testid="prop-submit" type="submit" disabled={busy} className="w-full">
            {busy ? (lang === "it" ? "Generazione mercato..." : "Generating market...") : t("save")}
          </Button>
        </form>
      </div>
    </div>
  );
}
